// src/components/Player/Player.jsx
import React, { useState, useEffect, useRef } from 'react';
import PlayerControls from './PlayerControls';
import PlaylistMenu from './PlaylistMenu';
import PermissionOverlay from '../UI/PermissionOverlay';
import { useDeviceOrientation } from '../../hooks/useDeviceOrientation';
import useParentPageInfo from '../../hooks/useParentPageInfo';
import {
  trackTourStarted,
  trackChapterStarted,
  trackAudioProgress,
  trackChapterComplete,
  trackTourComplete,
  trackView360,
  trackExit360,
  resetTourAnalyticsFlags,
  resetTrackAnalyticsFlags,
} from '../../utils/analytics';
import './Player.css';

// Import playlist data
import playlistData from '../../data/playlist.json';

const Player = () => {
  // State management
  const [state, setState] = useState({
    isPlaying: false,
    currentTrack: 0,
    isXRMode: false,
    exitingXR: false,
    pendingMessages: [],
    iframeReady: false,
    hasOrientationPermission: false,
    orientationPermissionRequested: false,
    playbackSpeed: 1,
    currentPlaylist: 0,
    playlist: null,
    volume: 1,
    isMuted: false,
    videoElement: null,
    isVideoSynced: false,
    showPermissionOverlay: false,
    currentTime: 0,
    duration: 0,
    progress: 0,
    isIOSInitialized: false,
    hasIOSAudioContext: false
  });

  const [isPlaylistVisible, setIsPlaylistVisible] = useState(false);
  const audioRef = useRef(null);
  const playerRef = useRef(null);
  const iframeRef = useRef(null);
  const audioContextRef = useRef(null);

  // Video preloader cache
  const videoPreloadCache = new Map();

  // Check if XR video is cached
  const isXRVideoCached = async (url) => {
    if ('caches' in window) {
      try {
        const cache = await caches.open('cmm-media-cache-v1');
        const response = await cache.match(url);
        const isCached = !!response;
        console.log(`🎬 XR video cache check for ${url}: ${isCached ? 'CACHED' : 'NOT CACHED'}`);
        return isCached;
      } catch (error) {
        console.error('🎬 Error checking XR video cache:', error);
        return false;
      }
    }
    return false;
  };

  // Preload single XR video
  const preloadXRVideo = async (url) => {
    if (videoPreloadCache.has(url)) {
      console.log('🎬 XR video already preloaded:', url);
      return true;
    }
    
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.preload = 'auto';
      video.src = url;
      
      // For faster start, we can just wait for metadata
      video.addEventListener('loadedmetadata', () => {
        videoPreloadCache.set(url, true);
        console.log('🎬 XR video preloaded successfully:', url);
        resolve(true);
      });
      
      video.addEventListener('canplaythrough', () => {
        videoPreloadCache.set(url, true);
        console.log('🎬 XR video can play through:', url);
        resolve(true);
      });
      
      video.addEventListener('error', (e) => {
        console.warn('🎬 Failed to preload XR video:', url, e);
        resolve(false);
      });
      
      // Trigger loading
      video.load();
      
      // Fallback in case events don't fire
      setTimeout(() => {
        if (!videoPreloadCache.has(url)) {
          console.warn('🎬 Preload timeout for:', url);
          resolve(false);
        }
      }, 15000); // 15 second timeout for XR videos
    });
  };

  // Preload adjacent XR videos (current and next track)
  const preloadAdjacentXRVideos = async (currentIndex) => {
    const tracksToPreload = [];
    
    // Current track
    if (state.playlist?.tracks[currentIndex]?.IsAR && state.playlist?.tracks[currentIndex]?.XR_Scene) {
      tracksToPreload.push(state.playlist.tracks[currentIndex].XR_Scene);
    }
    
    // Next track
    const nextIndex = (currentIndex + 1) % (state.playlist?.tracks?.length || 1);
    if (state.playlist?.tracks[nextIndex]?.IsAR && state.playlist?.tracks[nextIndex]?.XR_Scene) {
      tracksToPreload.push(state.playlist.tracks[nextIndex].XR_Scene);
    }
    
    await Promise.all(tracksToPreload.map(url => preloadXRVideo(url)));
  };

  const { 
    hasPermission, 
    showPermissionOverlay, 
    requestPermission, 
    skipPermission,
    error,
    isAndroid,
    isIOS
  } = useDeviceOrientation();

  const pageInfo = useParentPageInfo();

  // iOS-specific initialization
  useEffect(() => {
    const initializeIOSFeatures = async () => {
      const globalDeviceInfo = window.deviceInfo;
      const isIOSDevice = isIOS || (globalDeviceInfo && (globalDeviceInfo.isIOS || globalDeviceInfo.isIPad));
      
      if (isIOSDevice && !state.isIOSInitialized) {
        console.log('🍎 Initializing iOS-specific features...');
        
        try {
          // Initialize Web Audio Context for iOS (required for audio control)
          if (typeof AudioContext !== 'undefined' || typeof window.webkitAudioContext !== 'undefined') {
            const AudioContextClass = AudioContext || window.webkitAudioContext;
            audioContextRef.current = new AudioContextClass();
            
            // iOS requires user interaction to start audio context
            const resumeAudioContext = async () => {
              if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                try {
                  await audioContextRef.current.resume();
                  console.log('🍎 iOS AudioContext resumed');
                  setState(prev => ({ ...prev, hasIOSAudioContext: true }));
                } catch (e) {
                  console.warn('🍎 Failed to resume AudioContext:', e);
                }
              }
            };
            
            // Add touch event listeners for iOS audio context
            const handleUserInteraction = () => {
              resumeAudioContext();
              document.removeEventListener('touchstart', handleUserInteraction);
              document.removeEventListener('click', handleUserInteraction);
            };
            
            document.addEventListener('touchstart', handleUserInteraction, { once: true });
            document.addEventListener('click', handleUserInteraction, { once: true });
          }
          
          // iOS-specific error handling
          const handleIOSError = (error) => {
            console.error('🍎 iOS Error caught:', error);
            // Store iOS-specific errors for debugging
            if (!window.iosErrors) window.iosErrors = [];
            window.iosErrors.push({
              error: error.message || error.toString(),
              timestamp: new Date().toISOString(),
              component: 'Player'
            });
          };
          
          window.addEventListener('unhandledrejection', (event) => {
            if (isIOSDevice) {
              handleIOSError(event.reason);
            }
          });
          
          setState(prev => ({ ...prev, isIOSInitialized: true }));
          console.log('🍎 iOS features initialized successfully');
          
        } catch (error) {
          console.error('🍎 Error initializing iOS features:', error);
        }
      }
    };
    
    initializeIOSFeatures();
  }, [isIOS, state.isIOSInitialized]);

  // Initialize player with playlist data
  useEffect(() => {
    console.log('Player: Playlist selection effect triggered');
    console.log('Player: Current pageInfo:', pageInfo);
    console.log('Player: Available playlists:', playlistData?.playlists);

    if (!playlistData || !playlistData.playlists || playlistData.playlists.length === 0) {
      console.warn('Player: No playlist data available');
      return;
    }

    let selectedPlaylist = playlistData.playlists[0]; // Default
    console.log('Player: Default playlist:', selectedPlaylist.playlist_name);

    if (pageInfo && pageInfo.url) {
      const url = pageInfo.url.toLowerCase();
      console.log('Player: Parent page URL:', url);

      if (url.includes('dtla')) {
        console.log('Player: URL matches dtla');
        selectedPlaylist = playlistData.playlists.find(p => p.playlist_name === 'Ni de Aquí, Ni de Allá') || selectedPlaylist;
      } else if (url.includes('japantown')) {
        console.log('Player: URL matches japantown');
        selectedPlaylist = playlistData.playlists.find(p => p.playlist_name === 'Returning to the Harlem of the West') || selectedPlaylist;
      } else if (url.includes('chinatown')) {
        console.log('Player: URL matches chinatown');
        selectedPlaylist = playlistData.playlists.find(p => p.playlist_name === 'Look Up') || selectedPlaylist;
      } else if (url.includes('mission')) {
        console.log('Player: URL matches mission');
        selectedPlaylist = playlistData.playlists.find(p => p.playlist_name === 'Coffee Country') || selectedPlaylist;
      }
      console.log('Player: Selected playlist:', selectedPlaylist.playlist_name);
    } else {
      console.log('Player: No pageInfo or URL available, using default playlist');
    }

    resetTourAnalyticsFlags();
    trackTourStarted(selectedPlaylist);

    setupAudioElement();
    setState(prev => ({
      ...prev,
      playlist: selectedPlaylist,
      currentPlaylist: 0,
      currentTrack: 0
    }));
    console.log('Player: State updated with playlist:', selectedPlaylist.playlist_name);

    // Preload all XR videos in the background
    const preloadAllXRVideos = async () => {
      const preloadPromises = [];
      const xrVideos = [];
      
      for (const track of selectedPlaylist.tracks) {
        if (track.IsAR && track.XR_Scene && track.XR_Scene.trim() !== "") {
          xrVideos.push(track.XR_Scene);
          preloadPromises.push(preloadXRVideo(track.XR_Scene));
        }
      }
      
      if (xrVideos.length > 0) {
        console.log('🎬 Preloading all XR videos:', xrVideos);
        try {
          await Promise.all(preloadPromises);
          console.log('🎬 All XR videos preloaded successfully');
        } catch (error) {
          console.warn('🎬 Some XR videos failed to preload:', error);
        }
      }
    };

    // Start preloading in the background
    preloadAllXRVideos();
  }, [pageInfo]);

  // Handle track initialization after playlist is loaded
  useEffect(() => {
    if (state.playlist?.tracks && state.playlist.tracks.length > 0 && audioRef.current) {
      const firstTrack = state.playlist.tracks[0];
      console.log('Setting first track:', firstTrack);
      
      audioRef.current.src = firstTrack.audio_url;
      audioRef.current.load();
    }
  }, [state.playlist]);

  // Handle iframe messages
  useEffect(() => {
    const handleIframeMessage = (event) => {
      if (event.data.type === 'aframeReady') {
        console.log('A-Frame ready, syncing with audio time');
        setState(prev => ({ ...prev, iframeReady: true }));
        
        // Send any pending messages
        state.pendingMessages.forEach(msg => postMessageToIframe(msg));
        setState(prev => ({ ...prev, pendingMessages: [] }));

        // Sync with current audio time
        if (audioRef.current) {
          postMessageToIframe({
            action: 'setTime',
            time: audioRef.current.currentTime
          });
        }
      } else if (event.data.type === 'videoReady') {
        console.log('Video ready, syncing with audio time');
        if (audioRef.current) {
          postMessageToIframe({
            action: 'setTime',
            time: audioRef.current.currentTime
          });
          if (state.isPlaying) {
            postMessageToIframe({
              action: 'play',
              time: audioRef.current.currentTime
            });
          }
        } else if (event.data.type === 'currentTime') {
          console.log('Received video time:', event.data.time);
          if (state.exitingXR) {
            completeExitXRMode(event.data.time);
          }
        } else if (event.data.type === 'videoEnded') {
          console.log('Video ended, resetting audio');
          if (audioRef.current) {
            audioRef.current.currentTime = 0;
          }
          playNextTrack();
        }
      }
    };

    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, []);

  const setupAudioElement = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      
      // iOS-specific audio setup
      if (isIOS) {
        console.log('🍎 Setting up iOS-specific audio configuration');
        // Ensure audio is muted initially to allow autoplay on iOS
        audioRef.current.muted = false; // Don't mute by default
        audioRef.current.preload = 'metadata'; // Use metadata preload for iOS
        audioRef.current.crossOrigin = 'anonymous'; // CORS support
        
        // iOS requires these attributes
        audioRef.current.setAttribute('webkit-playsinline', 'true');
        audioRef.current.setAttribute('playsinline', 'true');
      }
      
      audioRef.current.addEventListener('timeupdate', handleTimeUpdate);
      audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      audioRef.current.addEventListener('ended', handleTrackEnd);
      audioRef.current.addEventListener('error', (e) => {
        console.error('Audio error:', e);
        if (isIOS) {
          console.error('🍎 iOS Audio error details:', {
            error: audioRef.current.error,
            networkState: audioRef.current.networkState,
            readyState: audioRef.current.readyState,
            src: audioRef.current.src
          });
        }
      });
      
      // iOS-specific audio event listeners
      if (isIOS) {
        audioRef.current.addEventListener('canplaythrough', () => {
          console.log('🍎 iOS audio can play through');
        });
        
        audioRef.current.addEventListener('loadstart', () => {
          console.log('🍎 iOS audio load started');
        });
        
        audioRef.current.addEventListener('progress', () => {
          console.log('🍎 iOS audio loading progress');
        });
        
        audioRef.current.addEventListener('suspend', () => {
          console.log('🍎 iOS audio suspended');
        });
        
        audioRef.current.addEventListener('stalled', () => {
          console.log('🍎 iOS audio stalled');
        });
      }
      
      // Set initial volume and playback speed
      audioRef.current.volume = state.volume;
      audioRef.current.playbackRate = state.playbackSpeed;
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime;
      const duration = audioRef.current.duration;
      const progress = (currentTime / duration) * 100;
      
      setState(prev => {
        // Analytics tracking for audio progress - use prev state to avoid closure issues
        if (prev.playlist && prev.playlist.tracks[prev.currentTrack]) {
          const track = prev.playlist.tracks[prev.currentTrack];
          
          // Only log when we reach milestone percentages
          if (progress >= 25 && progress < 26 && !window.audio25Fired) {
            console.log('handleTimeUpdate - 25% milestone reached for track:', track.title);
          }
          if (progress >= 50 && progress < 51 && !window.audio50Fired) {
            console.log('handleTimeUpdate - 50% milestone reached for track:', track.title);
          }
          if (progress >= 75 && progress < 76 && !window.audio75Fired) {
            console.log('handleTimeUpdate - 75% milestone reached for track:', track.title);
          }
          
          trackAudioProgress(track, progress);

          // Chapter complete when progress is near the end
          if (progress >= 99.5 && !window.chapterCompleteFired) {
            console.log('handleTimeUpdate - chapter complete triggered');
            trackChapterComplete(prev.playlist, track);
            window.chapterCompleteFired = true;
          }
        }
        
        return {
          ...prev,
          currentTime: currentTime,
          progress: progress
        };
      });
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      console.log('Audio metadata loaded:', {
        duration: audioRef.current.duration,
        currentTime: audioRef.current.currentTime
      });
      
      // iOS-specific handling
      if (isIOS) {
        console.log('🍎 iOS audio metadata loaded:', {
          duration: audioRef.current.duration,
          readyState: audioRef.current.readyState,
          networkState: audioRef.current.networkState
        });
      }
      
      setState(prev => ({
        ...prev,
        duration: audioRef.current.duration
      }));
    }
  };

  const handleTrackEnd = () => {
    console.log('handleTrackEnd called');
    console.log('Current state:', {
      playlist: state.playlist?.playlist_name,
      currentTrack: state.currentTrack,
      totalTracks: state.playlist?.tracks?.length,
      tracks: state.playlist?.tracks
    });
    
    // Use functional state update to ensure we have the latest state
    setState(prevState => {
      console.log('handleTrackEnd - prevState:', {
        playlist: prevState.playlist?.playlist_name,
        currentTrack: prevState.currentTrack,
        totalTracks: prevState.playlist?.tracks?.length
      });
      
      if (!prevState.playlist?.tracks) {
        console.error('No playlist tracks available in handleTrackEnd');
        return prevState;
      }

      if (prevState.currentTrack < prevState.playlist.tracks.length - 1) {
        console.log('Moving to next track:', prevState.currentTrack + 1);
        // Call setCurrentTrack with the next track index and playlist data
        setTimeout(() => setCurrentTrack(prevState.currentTrack + 1, prevState.playlist), 0);
        return prevState;
      } else {
        console.log('Last track finished, tour complete');
        // Last track finished, tour is complete
        trackTourComplete(prevState.playlist);
        return { ...prevState, isPlaying: false };
      }
    });
  };

  const setCurrentTrack = (index, playlistData = null) => {
    console.log('setCurrentTrack called with index:', index);
    console.log('Current state in setCurrentTrack:', {
      playlist: state.playlist?.playlist_name,
      tracks: state.playlist?.tracks,
      tracksLength: state.playlist?.tracks?.length
    });
    
    // Use passed playlist data if available, otherwise fall back to state
    const playlistToUse = playlistData || state.playlist;
    
    if (!playlistToUse?.tracks) {
      console.error('No playlist tracks available in setCurrentTrack');
      return;
    }

    const track = playlistToUse.tracks[index];
    if (!track) {
      console.error('Invalid track index:', index, 'Available tracks:', playlistToUse.tracks);
      return;
    }

    console.log('Setting current track:', track);
    
    resetTrackAnalyticsFlags();
    trackChapterStarted(playlistToUse, track, index);

    // Update state with new track and set playing to true
    setState(prev => ({ 
      ...prev, 
      currentTrack: index,
      isPlaying: true 
    }));

    // Preload adjacent XR videos in the background
    preloadAdjacentXRVideos(index).catch(error => {
      console.warn('🎬 Failed to preload adjacent XR videos:', error);
    });

    if (audioRef.current) {
      audioRef.current.src = track.audio_url;
      audioRef.current.load();
      
      // iOS-specific track change handling
      if (isIOS) {
        console.log('🍎 iOS track change - setting up new audio source');
        
        // Wait for load to complete before trying to play on iOS
        const handleCanPlay = () => {
          console.log('🍎 iOS audio can play, starting playback');
          audioRef.current.play().then(() => {
            console.log('🍎 iOS autoplay successful after track change');
          }).catch(e => {
            console.warn('🍎 iOS autoplay failed after track change:', e.message);
          });
          audioRef.current.removeEventListener('canplay', handleCanPlay);
        };
        
        audioRef.current.addEventListener('canplay', handleCanPlay);
      } else {
        // Autoplay the new track for non-iOS devices
        audioRef.current.play().catch(e => console.error('Error playing track:', e));
      }
    }
  };

  const handleTrackChange = (newTrackIndex) => {
    const newTrack = state.playlist.tracks[newTrackIndex];
    
    // If we're in XR mode, exit it first
    if (state.isXRMode) {
      // Force exit XR mode immediately
      setState(prev => ({
        ...prev,
        isXRMode: false,
        exitingXR: false
      }));
      
      // Remove XR content container and iframe
      const xrContent = document.getElementById('xrContent');
      if (xrContent) {
        // Remove all child elements (including iframe)
        while (xrContent.firstChild) {
          xrContent.removeChild(xrContent.firstChild);
        }
        // Remove the container itself
        xrContent.remove();
      }
      
      // Clear iframe reference
      iframeRef.current = null;
    }
    
    // Update current track
    setState(prev => ({
      ...prev,
      currentTrack: newTrackIndex
    }));
  };

  const playNextTrack = async () => {
    if (!state.playlist?.tracks) return;

    try {
      // Exit XR mode if currently in it
      if (state.isXRMode) {
        await exitXRMode();
      }

      const nextTrackIndex = (state.currentTrack + 1) % state.playlist.tracks.length;
      
      // Update current track
      setState(prev => ({
        ...prev,
        currentTrack: nextTrackIndex
      }));
      
      // Load and play the new track
      if (audioRef.current) {
        const nextTrack = state.playlist.tracks[nextTrackIndex];
        audioRef.current.src = nextTrack.audio_url;
        audioRef.current.load();
        // Ensure we start from beginning
        audioRef.current.currentTime = 0;
        
        // iOS-specific play handling
        if (isIOS) {
          const handleCanPlay = async () => {
            try {
              await audioRef.current.play();
              setState(prev => ({ ...prev, isPlaying: true }));
              console.log('🍎 iOS next track play successful');
            } catch (e) {
              console.warn('🍎 iOS next track play failed:', e.message);
              setState(prev => ({ ...prev, isPlaying: false }));
            }
            audioRef.current.removeEventListener('canplay', handleCanPlay);
          };
          audioRef.current.addEventListener('canplay', handleCanPlay);
        } else {
          await audioRef.current.play();
          setState(prev => ({ ...prev, isPlaying: true }));
        }
      }
    } catch (error) {
      console.error('Error playing next track:', error);
      if (isIOS) {
        console.error('🍎 iOS next track error:', error.message);
      }
      setState(prev => ({
        ...prev,
        isPlaying: false
      }));
    }
  };

  const playPreviousTrack = async () => {
    if (!state.playlist?.tracks) return;

    // If we're more than 3 seconds into the track, restart it instead
    if (audioRef.current && audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }

    try {
      // Exit XR mode if currently in it
      if (state.isXRMode) {
        await exitXRMode();
      }

      const prevTrackIndex = (state.currentTrack - 1 + state.playlist.tracks.length) % state.playlist.tracks.length;
      
      // Update current track
      setState(prev => ({
        ...prev,
        currentTrack: prevTrackIndex
      }));
      
      // Load and play the new track
      if (audioRef.current) {
        const prevTrack = state.playlist.tracks[prevTrackIndex];
        audioRef.current.src = prevTrack.audio_url;
        audioRef.current.load();
        
        // iOS-specific play handling
        if (isIOS) {
          const handleCanPlay = async () => {
            try {
              await audioRef.current.play();
              setState(prev => ({ ...prev, isPlaying: true }));
              console.log('🍎 iOS previous track play successful');
            } catch (e) {
              console.warn('🍎 iOS previous track play failed:', e.message);
              setState(prev => ({ ...prev, isPlaying: false }));
            }
            audioRef.current.removeEventListener('canplay', handleCanPlay);
          };
          audioRef.current.addEventListener('canplay', handleCanPlay);
        } else {
          await audioRef.current.play();
          setState(prev => ({ ...prev, isPlaying: true }));
        }
      }
    } catch (error) {
      console.error('Error playing previous track:', error);
      if (isIOS) {
        console.error('🍎 iOS previous track error:', error.message);
      }
      setState(prev => ({
        ...prev,
        isPlaying: false
      }));
    }
  };

  const togglePlay = () => {
    if (!audioRef.current || !state.playlist?.tracks) {
      console.error('Audio not initialized or no tracks available');
      return;
    }

    const newIsPlaying = !state.isPlaying;
    const currentTrack = state.playlist.tracks[state.currentTrack];
    
    if (newIsPlaying) {
      // iOS-specific audio play handling
      if (isIOS) {
        console.log('🍎 iOS play initiated');
        
        // Resume AudioContext if needed
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume().then(() => {
            console.log('🍎 iOS AudioContext resumed for play');
          }).catch(e => {
            console.warn('🍎 Failed to resume AudioContext:', e);
          });
        }
      }
      
      // Ensure we're at the right track before playing
      if (audioRef.current.src !== currentTrack.audio_url) {
        audioRef.current.src = currentTrack.audio_url;
        audioRef.current.load();
      }
      
      // Play audio with iOS error handling
      audioRef.current.play().then(() => {
        console.log('Audio play successful');
        if (isIOS) {
          console.log('🍎 iOS audio playback started successfully');
        }
      }).catch(e => {
        console.error('Error playing audio:', e);
        if (isIOS) {
          console.error('🍎 iOS audio play failed:', e.name, e.message);
          // Common iOS audio play errors and solutions
          if (e.name === 'NotAllowedError') {
            console.log('🍎 iOS audio play blocked - user interaction required');
          } else if (e.name === 'NotSupportedError') {
            console.log('🍎 iOS audio format not supported');
          }
        }
      });
      
      // Sync video if in XR mode
      if (state.isXRMode) {
        postMessageToIframe({
          action: 'play',
          time: audioRef.current.currentTime
        });
      }
    } else {
      // Pause audio
      audioRef.current.pause();
      
      // Pause video if in XR mode
      if (state.isXRMode) {
        postMessageToIframe({ action: 'pause' });
      }
    }
    
    setState(prev => ({ ...prev, isPlaying: newIsPlaying }));
  };

  const toggleXR = async () => {
    const currentTrack = state.playlist.tracks[state.currentTrack];
    if (currentTrack.IsAR && currentTrack.XR_Scene) {
      if (state.isXRMode) {
        await exitXRMode();
      } else {
        await enterXRMode();
      }
    }
  };

  const enterXRMode = async () => {
    if (!state.playlist || !state.playlist.tracks[state.currentTrack]) return;
    
    const currentTrack = state.playlist.tracks[state.currentTrack];
    if (!currentTrack.IsAR || !currentTrack.XR_Scene) {
        console.warn("No XR content available for this track.");
        return;
    }

    trackView360(state.playlist, currentTrack);

    // Enhanced iOS device check using global device info
    const globalDeviceInfo = window.deviceInfo;
    const isIOSDevice = isIOS || (globalDeviceInfo && (globalDeviceInfo.isIOS || globalDeviceInfo.isIPad));
    const isIPadDevice = globalDeviceInfo?.isIPad || /iPad/.test(navigator.userAgent) || 
                        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // Check for device orientation permission (mainly for iOS)
    if (!hasPermission && isIOSDevice) {
      console.log('🍎 Requesting device orientation permission for iOS device');
      const granted = await requestPermission();
      if (!granted) {
        console.warn("🍎 Device orientation permission not granted");
        // Continue anyway - user can still use touch controls
      }
    }

    // Show loading state with iOS-specific messaging
    const loadingOverlay = document.createElement('div');
    loadingOverlay.className = 'xr-loading-overlay';
    loadingOverlay.innerHTML = `
      <div class="xr-loading-message" style="color: white; text-align: center;">
        Loading 360° experience...<br>
        ${isIOSDevice ? '🍎 Optimizing for iOS...<br>' : ''}
        This may take up to ${isIOSDevice ? '30' : '20'} seconds on slower connections
      </div>
      <div class="xr-loading-spinner"></div>
    `;
    document.body.appendChild(loadingOverlay);

    try {
      // Update state FIRST
      setState(prev => ({
        ...prev,
        isXRMode: true,
        exitingXR: false
      }));

      // Store playback state
      const wasPlaying = state.isPlaying;
      const currentTime = audioRef.current?.currentTime || 0;

      // Check if video is cached first
      const isCached = await isXRVideoCached(currentTrack.XR_Scene);
      if (isCached) {
        console.log('🎬 XR video is cached, loading should be faster');
        loadingOverlay.querySelector('.xr-loading-message').innerHTML = `
          Loading 360° experience...<br>
          <small>Using cached content</small>
        `;
      }

      // Preload video first
      const videoReady = await preloadXRVideo(currentTrack.XR_Scene);
      if (!videoReady) {
        throw new Error('Video failed to load');
      }

      // Setup XR scene with iOS-specific callback
      setupXRScene(currentTrack.XR_Scene, () => {
        // When scene is loaded, remove loading overlay
        loadingOverlay.style.opacity = '0';
        setTimeout(() => {
          if (document.body.contains(loadingOverlay)) {
            document.body.removeChild(loadingOverlay);
          }
        }, 500);

        // Sync with audio player
        postMessageToIframe({
          action: 'setTime',
          time: currentTime
        });

        // Enhanced playback restoration for iOS
        if (wasPlaying) {
          const restorePlayback = () => {
            console.log('🎬 Restoring playback for XR mode');
            if (isIOSDevice) {
              console.log('🍎 iOS XR playback restoration');
            }
            postMessageToIframe({
              action: 'play',
              time: currentTime
            });
          };

          // Immediate attempt
          restorePlayback();
          
          // Additional attempts for iOS reliability
          if (isIOSDevice) {
            setTimeout(restorePlayback, 1000);
            setTimeout(restorePlayback, 2000);
          } else {
            setTimeout(restorePlayback, 500);
          }
        }

        // Enhanced device orientation setup for iOS
        setTimeout(() => {
          postMessageToIframe({
            action: 'enableDeviceOrientation',
            hasPermission: hasPermission,
            isAndroid: isAndroid,
            isIOS: isIOSDevice,
            isIPad: isIPadDevice
          });
        }, 1000);

        // Enhanced mobile initialization with iOS-specific handling
        setTimeout(() => {
          console.log('🎬 Sending enhanced mobile initialization');
          const mobileInfo = {
            action: 'initializeMobile',
            isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
            isIOS: isIOSDevice,
            isIPad: isIPadDevice,
            isAndroid: isAndroid,
            isChromeOnIOS: globalDeviceInfo?.isChromeOnIOS || false,
            devicePixelRatio: window.devicePixelRatio,
            maxTouchPoints: navigator.maxTouchPoints,
            userAgent: navigator.userAgent.substring(0, 100)
          };
          
          console.log('🍎 iOS mobile info:', mobileInfo);
          postMessageToIframe(mobileInfo);
        }, isIOSDevice ? 2000 : 1500); // Longer delay for iOS
      }, isIOSDevice); // Pass iOS flag to setupXRScene
    } catch (error) {
      console.error('Error entering XR mode:', error);
      if (isIOSDevice) {
        console.error('🍎 iOS XR mode error:', error.message);
      }
      if (document.body.contains(loadingOverlay)) {
        document.body.removeChild(loadingOverlay);
      }
      setState(prev => ({
        ...prev,
        isXRMode: false,
        exitingXR: false
      }));
    }
  };

  const exitXRMode = async () => {
    if (!state.isXRMode) return;

    if (state.playlist && state.playlist.tracks[state.currentTrack]) {
      const currentTrack = state.playlist.tracks[state.currentTrack];
      trackExit360(state.playlist, currentTrack);
    }

    console.log('Exiting XR mode');
    if (isIOS) {
      console.log('🍎 iOS XR mode exit');
    }
    
    setState(prev => ({ ...prev, isXRMode: false, exitingXR: true }));
    
    // First pause the video
    postMessageToIframe({ action: 'pause' });
    
    // Get current time from iframe
    postMessageToIframe({ action: 'getCurrentTime' });
    
    // Clean up XR content immediately
    const xrContent = document.getElementById('xrContent');
    if (xrContent) {
      xrContent.innerHTML = '';
    }
    
    // Clear iframe reference
    iframeRef.current = null;
    
    // Set a timeout fallback (longer for iOS)
    const fallbackTimeout = isIOS ? 2000 : 1000;
    setTimeout(() => {
      if (state.exitingXR) {
        console.log('Fallback: completing XR exit');
        if (isIOS) {
          console.log('🍎 iOS XR exit fallback triggered');
        }
        completeExitXRMode(0);
      }
    }, fallbackTimeout);
  };

  const completeExitXRMode = (videoTime) => {
    console.log('Completing XR exit with time:', videoTime);
    if (isIOS) {
      console.log('🍎 iOS XR exit completion');
    }
    
    // Update state
    setState(prev => ({
      ...prev,
      exitingXR: false,
      isXRMode: false
    }));

    // Clean up iframe
    const xrContent = document.getElementById('xrContent');
    if (xrContent) {
      xrContent.innerHTML = '';
      xrContent.style.display = 'none';
    }

    // Show audio content
    const audioContent = document.querySelector('.audio-content');
    if (audioContent) {
      audioContent.style.display = 'flex';
    }

    // Check if we're at the end of the track
    const atEnd = videoTime >= (audioRef.current?.duration - 0.5); // 0.5 second threshold

    // Reset to beginning if at end or if this was triggered by video ended
    const newTime = atEnd ? 0 : videoTime;
    
    if (audioRef.current) {
      if (!atEnd) {
        audioRef.current.currentTime = videoTime;
        if (state.isPlaying) {
          // iOS-specific audio resume
          if (isIOS) {
            console.log('🍎 iOS audio resume after XR exit');
            audioRef.current.play().then(() => {
              console.log('🍎 iOS audio resumed successfully');
            }).catch(e => {
              console.warn('🍎 iOS audio resume failed:', e.message);
            });
          } else {
            audioRef.current.play().catch(console.error);
          }
        }
      } else {
        // At end - ensure paused state
        audioRef.current.currentTime = 0;
        setState(prev => ({ ...prev, isPlaying: false }));
      }
    }
  };

  const setupXRScene = (videoUrl, onReadyCallback, isIOSDevice = false) => {
    console.log('Setting up XR scene with video URL:', videoUrl);
    if (isIOSDevice) {
      console.log('🍎 Setting up iOS-optimized XR scene');
    }
    
    // Create or get the XR content container
    let xrContent = document.getElementById('xrContent');
    if (!xrContent) {
      xrContent = document.createElement('div');
      xrContent.id = 'xrContent';
      xrContent.className = 'xr-content';
      document.body.appendChild(xrContent);
    }

    // Clear previous iframe
    xrContent.innerHTML = '';
    
    // Create new iframe
    const iframe = document.createElement('iframe');
    iframe.id = 'videoFrame';
    iframe.className = 'video-frame';
    iframe.allowFullscreen = true;
    
    // iOS-specific iframe attributes
    if (isIOSDevice) {
      iframe.setAttribute('allow', 'autoplay; fullscreen; gyroscope; accelerometer');
      iframe.setAttribute('allowfullscreen', 'true');
      iframe.setAttribute('webkitallowfullscreen', 'true');
      iframe.setAttribute('mozallowfullscreen', 'true');
    }
    
    xrContent.appendChild(iframe);

    // Enhanced CSS for iOS compatibility
    const hideUI = `
      <style>
        .a-loader-title, .a-enter-vr-button, .a-loader {
          display: none !important;
        }
        body {
          background-color: #182F48 !important;
          ${isIOSDevice ? '-webkit-overflow-scrolling: touch; overflow: hidden;' : ''}
        }
        ${isIOSDevice ? `
        /* iOS-specific styles */
        .a-canvas {
          -webkit-transform: translate3d(0,0,0);
          transform: translate3d(0,0,0);
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
        }
        ` : ''}
      </style>
    `;
    
    // Enhanced iframe content with iOS optimizations
    iframe.srcdoc = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no">
        <meta name="apple-mobile-web-app-capable" content="yes">
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent">
        <meta name="mobile-web-app-capable" content="yes">
        ${isIOSDevice ? '<meta name="format-detection" content="telephone=no">' : ''}
        <title>360 Video</title>
        <script src="/cmm-single-build-player/libs/aframe-v1.7.1.min.js"></script>
        ${hideUI}
        <style>
          body { 
            margin: 0; 
            overflow: hidden;
            ${isIOSDevice ? '-webkit-overflow-scrolling: touch; -webkit-user-select: none;' : ''}
          }
          .a-canvas { 
            background: #000 !important;
            touch-action: pan-x pan-y;
            ${isIOSDevice ? '-webkit-tap-highlight-color: transparent;' : ''}
          }
          html, body {
            height: 100%;
            width: 100%;
            position: fixed;
            top: 0;
            left: 0;
          }
        </style>
      </head>
      <body>
        <a-scene device-orientation-permission-ui="enabled: false"
                 loading-screen="enabled: false"
                 vr-mode-ui="enabled: false"
                 embedded
                 background="color: #000"
                 style="height: 100vh; width: 100vw;"> 
          <a-assets>
            <video id="xrVideo"
                   src="${videoUrl}"
                   crossorigin="anonymous"
                   playsinline
                   webkit-playsinline
                   muted
                   preload="${isIOSDevice ? 'metadata' : 'auto'}"
                   controls="false"
                   ${isIOSDevice ? 'autoplay="false"' : ''}
                   xr-layer>
            </video>
          </a-assets>
          
          <a-videosphere src="#xrVideo" rotation="0 -90 0"></a-videosphere>
        
          <a-entity position="0 1.6 0">
            <a-camera
                look-controls="enabled: true;
                            pointerLockEnabled: false;
                            reverseMouseDrag: false;
                            touchEnabled: true;
                            magicWindowTrackingEnabled: true;
                            mouseEnabled: true;
                            reverseTouchDrag: false"
                wasd-controls="enabled: false"
                position="0 0 0">
            </a-camera>
          </a-entity>

          <script>
            const video = document.getElementById('xrVideo');
            video.muted = true;
            
            // Enhanced device detection
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
            const isIPad = /iPad/.test(navigator.userAgent) || 
                          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
            const isAndroid = /Android/i.test(navigator.userAgent);
            const isChromeOnIOS = /CriOS|Chrome/.test(navigator.userAgent) && isIOS;
            
            console.log('🎬 Enhanced XR iframe initialized:', {
              videoUrl: video.src,
              isMobile, isIOS, isIPad, isAndroid, isChromeOnIOS,
              userAgent: navigator.userAgent.substring(0, 50)
            });

            function syncVideo(time) {
              if (Math.abs(video.currentTime - time) > 0.1) {
                video.currentTime = time;
              }
            }

            // Track if we've already notified parent that we're ready
            let readyNotificationSent = false;
            
            function notifyReady() {
              if (readyNotificationSent) {
                console.log('🎬 Ready notification already sent, skipping...');
                return;
              }
              
              console.log('🎬 XR iframe ready, video readyState:', video.readyState);
              if (isIOS) {
                console.log('🍎 iOS XR iframe ready');
              }
              readyNotificationSent = true;
              window.parent.postMessage({ 
                type: 'aframeReady'
              }, '*');
            }

            // Track if mobile video has been initialized to prevent loops
            let mobileVideoInitialized = false;
            
            // Enhanced mobile video initialization with iOS-specific handling
            function initializeMobileVideo() {
              if (mobileVideoInitialized) {
                console.log('📱 Mobile video already initialized, skipping...');
                return;
              }
              
              console.log('📱 Initializing mobile video...');
              if (isIOS) {
                console.log('🍎 iOS video initialization');
              }
              mobileVideoInitialized = true;
              
              // iOS-specific video initialization
              if (isIOS) {
                // Don't auto-play on iOS initially
                video.muted = true;
                video.setAttribute('playsinline', 'true');
                video.setAttribute('webkit-playsinline', 'true');
                
                // Load the video but don't play
                video.load();
                console.log('🍎 iOS video loaded, waiting for play command');
              } else {
                // Try to play and pause immediately to "prime" the video on Android
                const playPromise = video.play();
                if (playPromise !== undefined) {
                  playPromise.then(() => {
                    console.log('📱 Mobile video primed successfully');
                    video.pause();
                    video.currentTime = 0;
                  }).catch(e => {
                    console.log('📱 Mobile video prime failed (expected on some devices):', e.message);
                  });
                }
              }
            }

            video.addEventListener('loadedmetadata', function() {
              console.log('🎬 Video metadata loaded:', {
                duration: video.duration,
                readyState: video.readyState,
                videoWidth: video.videoWidth,
                videoHeight: video.videoHeight
              });
              
              if (isIOS) {
                console.log('🍎 iOS video metadata loaded');
              }
              
              // Initialize mobile video handling only once
              if (isMobile && !mobileVideoInitialized) {
                initializeMobileVideo();
              }
              
              notifyReady();
            });

            video.addEventListener('canplaythrough', function() {
              console.log('🎬 Video can play through, readyState:', video.readyState);
              if (isIOS) {
                console.log('🍎 iOS video can play through');
              }
            });

            // Enhanced error handling with iOS-specific recovery
            let errorRecoveryAttempts = 0;
            const maxErrorRecoveryAttempts = isIOS ? 3 : 2; // More attempts for iOS
            
            video.addEventListener('error', function(e) {
              console.error('🎬 Video error:', e);
              if (isIOS) {
                console.error('🍎 iOS video error');
              }
              
              console.error('🎬 Video error details:', {
                code: video.error?.code,
                message: video.error?.message,
                networkState: video.networkState,
                readyState: video.readyState
              });
              
              // Try to recover from error only if we haven't exceeded max attempts
              if (errorRecoveryAttempts < maxErrorRecoveryAttempts) {
                errorRecoveryAttempts++;
                const retryDelay = isIOS ? 2000 : 1000; // Longer delay for iOS
                setTimeout(() => {
                  console.log('🎬 Attempting video recovery... (attempt', errorRecoveryAttempts, 'of', maxErrorRecoveryAttempts, ')');
                  if (isIOS) {
                    console.log('🍎 iOS video recovery attempt');
                  }
                  video.load();
                }, retryDelay);
              } else {
                console.error('🎬 Max error recovery attempts reached, giving up');
                if (isIOS) {
                  console.error('🍎 iOS video recovery failed');
                }
              }
            });

            video.addEventListener('ended', () => {
              console.log('🎬 Video ended');
              if (isIOS) {
                console.log('🍎 iOS video ended');
              }
              window.parent.postMessage({
                type: 'videoEnded'
              }, '*');
            });

            // Additional iOS-specific event listeners
            if (isIOS) {
              video.addEventListener('play', function() {
                console.log('🍎 iOS video started playing');
              });

              video.addEventListener('pause', function() {
                console.log('🍎 iOS video paused');
              });

              video.addEventListener('waiting', function() {
                console.log('🍎 iOS video waiting for data');
              });

              video.addEventListener('stalled', function() {
                console.log('🍎 iOS video stalled');
              });

              video.addEventListener('suspend', function() {
                console.log('🍎 iOS video suspended');
              });
            }
            
            window.addEventListener('message', (event) => {
              if (!video) return;
              
              console.log('🎬 XR iframe received message:', event.data);
              if (isIOS) {
                console.log('🍎 iOS XR message:', event.data.action);
              }
              
              switch(event.data.action) {
                case 'play':
                  console.log('🎬 Play command received, syncing to time:', event.data.time || 0);
                  if (isIOS) {
                    console.log('🍎 iOS play command');
                  }
                  syncVideo(event.data.time || 0);
                  
                  // Enhanced iOS play handling
                  const playPromise = video.play();
                  if (playPromise !== undefined) {
                    playPromise.then(() => {
                      console.log('🎬 Video playing successfully');
                      if (isIOS) {
                        console.log('🍎 iOS video play successful');
                      }
                      // Double-check sync after play starts
                      setTimeout(() => {
                        syncVideo(event.data.time || 0);
                      }, isIOS ? 200 : 100); // Longer delay for iOS
                    }).catch(e => {
                      console.error('🎬 Video play failed:', e);
                      if (isIOS) {
                        console.error('🍎 iOS video play failed:', e.message);
                      }
                      
                      // Enhanced iOS recovery
                      if (isIOS && errorRecoveryAttempts < maxErrorRecoveryAttempts) {
                        console.log('🍎 iOS play failed, trying recovery...');
                        errorRecoveryAttempts++;
                        setTimeout(() => {
                          video.load();
                          setTimeout(() => {
                            video.play().catch(e2 => {
                              console.error('🍎 iOS video recovery play failed:', e2);
                            });
                          }, 1000);
                        }, 1000);
                      }
                    });
                  }
                  break;
                  
                case 'pause':
                  console.log('🎬 Pause command received');
                  if (isIOS) {
                    console.log('🍎 iOS pause command');
                  }
                  video.pause();
                  break;
                  
                case 'setTime':
                  console.log('🎬 Set time command received:', event.data.time);
                  if (isIOS) {
                    console.log('🍎 iOS set time command');
                  }
                  syncVideo(event.data.time);
                  break;
                  
                case 'getCurrentTime':
                  console.log('🎬 Get current time command received');
                  if (isIOS) {
                    console.log('🍎 iOS get current time command');
                  }
                  window.parent.postMessage({
                    type: 'currentTime',
                    time: video.currentTime
                  }, '*');
                  break;
                  
                case 'enableDeviceOrientation':
                  console.log('🎬 Enable device orientation command received:', event.data);
                  if (isIOS) {
                    console.log('🍎 iOS device orientation setup');
                  }
                  enableDeviceOrientationTracking(event.data.hasPermission, event.data.isAndroid, event.data.isIOS, event.data.isIPad);
                  break;
                  
                case 'initializeMobile':
                  console.log('📱 Initialize mobile command received:', event.data);
                  if (event.data.isIOS) {
                    console.log('🍎 iOS mobile initialization command');
                  }
                  
                  if (event.data.isMobile) {
                    console.log('📱 Running mobile-specific initialization...');
                    
                    // Initialize mobile video if not already done
                    if (!mobileVideoInitialized) {
                      initializeMobileVideo();
                    }
                    
                    // Enhanced A-Frame initialization delay for iOS
                    const initDelay = event.data.isIOS ? 500 : 100;
                    setTimeout(() => {
                      const scene = document.querySelector('a-scene');
                      const videosphere = document.querySelector('a-videosphere');
                      
                      if (scene && videosphere) {
                        console.log('📱 Refreshing A-Frame components for mobile...');
                        if (event.data.isIOS) {
                          console.log('🍎 iOS A-Frame component refresh');
                        }
                        
                        // Force videosphere to re-initialize
                        videosphere.setAttribute('src', '#xrVideo');
                        
                        // Trigger material update
                        if (videosphere.components && videosphere.components.material) {
                          videosphere.components.material.material.map.needsUpdate = true;
                        }
                      }
                    }, initDelay);
                  }
                  break;
              }
            });

            // Enhanced initialization for iOS
            if (video.readyState > 3) {
              notifyReady();
            }

            // Longer timeout for iOS devices
            const readyTimeout = isIOS ? 8000 : 5000;
            setTimeout(() => {
              if (video.readyState === 0) {
                console.warn('XR video still loading after timeout, readyState:', video.readyState);
                if (isIOS) {
                  console.warn('🍎 iOS video loading timeout');
                }
                notifyReady();
              }
            }, readyTimeout);

            // Enhanced device orientation setup with iOS-specific handling
            console.log('Setting up device orientation for 360° video');
            if (isIOS) {
              console.log('🍎 iOS device orientation setup');
            }
            
            // Enhanced device orientation test
            if (typeof DeviceOrientationEvent !== 'undefined') {
              console.log('DeviceOrientationEvent supported');
              
              // Test device orientation is working
              const testListener = (event) => {
                if (event.alpha !== null || event.beta !== null || event.gamma !== null) {
                  console.log('Device orientation working:', {
                    alpha: Math.round(event.alpha || 0),
                    beta: Math.round(event.beta || 0),
                    gamma: Math.round(event.gamma || 0)
                  });
                  if (isIOS) {
                    console.log('🍎 iOS device orientation working');
                  }
                  window.removeEventListener('deviceorientation', testListener);
                }
              };
              window.addEventListener('deviceorientation', testListener);
              
              // Extended timeout for iOS
              const orientationTimeout = isIOS ? 8000 : 5000;
              setTimeout(() => {
                window.removeEventListener('deviceorientation', testListener);
              }, orientationTimeout);
            }

            // Wait for A-Frame to be ready
            document.addEventListener('DOMContentLoaded', () => {
              console.log('A-Frame scene ready');
              if (isIOS) {
                console.log('🍎 iOS A-Frame scene ready');
              }
            });

            // Enhanced function to enable device orientation tracking
            function enableDeviceOrientationTracking(hasPermission, isAndroid, isIOSDevice, isIPadDevice) {
              console.log('Enabling device orientation tracking:', { hasPermission, isAndroid, isIOSDevice, isIPadDevice });
              if (isIOSDevice) {
                console.log('🍎 iOS device orientation tracking enabled');
              }
              
              const scene = document.querySelector('a-scene');
              const camera = document.querySelector('a-camera');
              
              if (camera && camera.components && camera.components['look-controls']) {
                const lookControls = camera.components['look-controls'];
                console.log('Look controls available, ensuring magic window tracking');
                
                // Ensure magic window tracking is enabled for device orientation
                if (lookControls.data) {
                  lookControls.data.magicWindowTrackingEnabled = true;
                  if (isIOSDevice) {
                    console.log('🍎 iOS magic window tracking enabled');
                  }
                }
                
                // Enhanced device orientation test for iOS
                if (typeof DeviceOrientationEvent !== 'undefined') {
                  let eventReceived = false;
                  const testTimeout = isIOSDevice ? 5000 : 3000; // Longer for iOS
                  
                  const testListener = (event) => {
                    if (!eventReceived && (event.alpha !== null || event.beta !== null || event.gamma !== null)) {
                      eventReceived = true;
                      console.log('Device orientation working in iframe:', {
                        alpha: Math.round(event.alpha || 0),
                        beta: Math.round(event.beta || 0),
                        gamma: Math.round(event.gamma || 0)
                      });
                      if (isIOSDevice) {
                        console.log('🍎 iOS device orientation working in iframe');
                      }
                      window.removeEventListener('deviceorientation', testListener);
                    }
                  };
                  
                  window.addEventListener('deviceorientation', testListener);
                  setTimeout(() => {
                    if (!eventReceived) {
                      console.warn('No device orientation events in iframe - using touch/mouse controls');
                      if (isIOSDevice) {
                        console.warn('🍎 iOS device orientation not working - using touch controls');
                      }
                    }
                    window.removeEventListener('deviceorientation', testListener);
                  }, testTimeout);
                }
              }
            }

            // Enhanced A-Frame scene initialization with iOS-specific handling
            const sceneInitTimeout = isIOS ? 3000 : 2000;
            setTimeout(() => {
              const scene = document.querySelector('a-scene');
              const videosphere = document.querySelector('a-videosphere');
              
              if (scene && scene.hasLoaded) {
                console.log('🎬 A-Frame scene loaded and ready');
                if (isIOS) {
                  console.log('🍎 iOS A-Frame scene loaded');
                }
                
                // Enhanced mobile-specific A-Frame initialization
                if (isMobile) {
                  console.log('📱 Initializing A-Frame for mobile...');
                  if (isIOS) {
                    console.log('🍎 iOS A-Frame mobile initialization');
                  }
                  
                  // Ensure videosphere is properly connected to video
                  if (videosphere && video) {
                    console.log('📱 Connecting videosphere to video element');
                    videosphere.setAttribute('src', '#xrVideo');
                    
                    // Force refresh of videosphere
                    if (videosphere.components && videosphere.components.material) {
                      videosphere.components.material.material.map.needsUpdate = true;
                    }
                  }
                }
              } else {
                console.warn('🎬 A-Frame scene not loaded yet');
                if (isIOS) {
                  console.warn('🍎 iOS A-Frame scene loading delayed');
                }
              }
            }, sceneInitTimeout);
            
            // Additional scene ready events
            document.addEventListener('DOMContentLoaded', () => {
              console.log('🎬 DOM Content Loaded');
            });
            
            // A-Frame specific ready event
            window.addEventListener('load', () => {
              console.log('🎬 Window loaded');
              const scene = document.querySelector('a-scene');
              if (scene) {
                scene.addEventListener('loaded', () => {
                  console.log('🎬 A-Frame scene loaded event fired');
                  if (isIOS) {
                    console.log('🍎 iOS A-Frame scene loaded event');
                  }
                });
              }
            });
          </script>
        </a-scene>
      </body>
      </html>
    `;

    // Handle iframe load event with iOS-specific handling
    iframe.onload = () => {
      console.log('XR iframe loaded');
      if (isIOSDevice) {
        console.log('🍎 iOS XR iframe loaded');
      }
      setState(prev => ({ ...prev, iframeReady: true }));
      if (onReadyCallback) onReadyCallback();
    };
    
    // Enhanced error handling for iframe
    iframe.onerror = (error) => {
      console.error('XR iframe load error:', error);
      if (isIOSDevice) {
        console.error('🍎 iOS XR iframe load error');
      }
    };
    
    iframeRef.current = iframe;
  };

  const postMessageToIframe = (message) => {
    if (!state.iframeReady) {
      setState(prev => ({
        ...prev,
        pendingMessages: [...prev.pendingMessages, message]
      }));
      return;
    }
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage(message, '*');
    }
  };

  const handleSeek = (event) => {
    if (!audioRef.current || !state.playlist?.tracks) return;

    const progressBar = event.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickPosition = (event.clientX - rect.left) / rect.width;
    const newTime = clickPosition * audioRef.current.duration;

    // Update audio position
    audioRef.current.currentTime = newTime;

    // If in XR mode, sync video position
    if (state.isXRMode) {
      postMessageToIframe({
        action: 'setTime',
        time: newTime
      });
    }

    // Update state
    setState(prev => ({
      ...prev,
      currentTime: newTime,
      progress: clickPosition * 100
    }));
  };

  // Volume change handler
  const handleVolumeChange = (newVolume) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    
    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }
    
    setState(prev => ({
      ...prev,
      volume: clampedVolume,
      isMuted: clampedVolume === 0
    }));
  };

  // Toggle mute
  const toggleMute = () => {
    if (state.isMuted) {
      // Unmute - restore previous volume or default to 0.5
      const newVolume = state.volume > 0 ? state.volume : 0.5;
      handleVolumeChange(newVolume);
    } else {
      // Mute
      setState(prev => ({
        ...prev,
        isMuted: true
      }));
      if (audioRef.current) {
        audioRef.current.volume = 0;
      }
    }
  };

  // Playback speed change handler
  const handlePlaybackSpeedChange = (newSpeed) => {
    const validSpeeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const clampedSpeed = validSpeeds.includes(newSpeed) ? newSpeed : 1;
    
    if (audioRef.current) {
      audioRef.current.playbackRate = clampedSpeed;
    }
    
    setState(prev => ({
      ...prev,
      playbackSpeed: clampedSpeed
    }));
  };

  // Cycle through playback speeds
  const cyclePlaybackSpeed = () => {
    const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = speeds.indexOf(state.playbackSpeed);
    const nextIndex = (currentIndex + 1) % speeds.length;
    handlePlaybackSpeedChange(speeds[nextIndex]);
  };

  const currentTrack = state.playlist?.tracks[state.currentTrack];

  return (
    <div className="player-container" ref={playerRef}>
      {showPermissionOverlay && (
        <PermissionOverlay
          onEnableMotion={requestPermission}
          onSkip={skipPermission}
          error={error}
          isAndroid={isAndroid}
        />
      )}
      <div className="player-content">
        <div id="xrContent" className={`xr-content ${state.isXRMode ? 'active' : ''}`}>
          {/* XR iframe will be inserted here */}
        </div>
        <div className={`audio-content ${state.isXRMode ? '' : 'active'}`}>
          <div className="artwork-container">
            <img
              src={currentTrack?.artwork_url}
              alt={currentTrack?.title}
              className="artwork-image"
            />
          </div>
          <div className="track-info">
            <h2 className="playlist-name">{state.playlist?.playlist_name}</h2>
            <h3 className="chapter-title">{currentTrack?.title}</h3>
          </div>
        </div>
        
        <div className="player-overlay">
          <PlayerControls
            state={state}
            onPlayPause={togglePlay}
            onNext={playNextTrack}
            onPrevious={playPreviousTrack}
            onToggleXR={toggleXR}
            onTogglePlaylist={() => setIsPlaylistVisible(!isPlaylistVisible)}
            onSeek={handleSeek}
            onVolumeChange={handleVolumeChange}
            onToggleMute={toggleMute}
            onPlaybackSpeedChange={handlePlaybackSpeedChange}
            onCyclePlaybackSpeed={cyclePlaybackSpeed}
          />
        </div>
      </div>

      <PlaylistMenu
        isVisible={isPlaylistVisible}
        playlist={state.playlist}
        currentTrack={state.currentTrack}
        onTrackSelect={setCurrentTrack}
        onClose={() => setIsPlaylistVisible(false)}
      />
    </div>
  );
};

export default Player;